import { useCallback, useEffect, useRef, useState } from 'react';

import classNames from 'classnames';
import { observer } from 'mobx-react';

import { useCoinsAnimation } from '../../hooks/useCoinsAnimation';
import { analytics } from '../../services/analytics';
import { resolveFlag, useFlag } from '../../services/launchDarkly';
import { useStores } from '../../stores/useStores';
import { devError, devLog } from '../../utils/log';
import { ShareDialog } from '../Dialogs/GameUiDialog/ShareDialog';
import { Loader } from '../Loader/Loader';

import styles from './MetagamesWarmFrame.module.scss';

/**
 * A metagames-only game-ui iframe, booted before the player clicks so opening a metagame costs no boot.
 *
 * Wired for Prize Drop, Trail, Hammers, Puzzle and sports. The warm trigger is lobby load
 * rather than a widget entering the viewport — see
 * game-ui/docs/superpowers/specs/2026-08-12-metagame-warm-iframe-design.md.
 */
/** Forces warm on without touching the flag, for testing. Drop once the rollout is settled. */
const WARM_LAUNCH_OVERRIDE_PARAM = 'warmLaunch';

/**
 * How long a launch may take before the loader appears. A warm launch often beats this, in which case
 * the player never sees a loader at all — backdrop fades in, game fades in. Showing a spinner for
 * 200ms and then removing it is more visible motion than showing nothing.
 */
const LOADER_DELAY_MS = 400;

/** Safety net for the reveal — see the effect below. Long enough that a normal load reveals on its own. */
const REVEAL_CAP_MS = 3000;

export const MetagamesWarmFrame = observer(() => {
  const {
    metagamesWarmStore,
    prizeDropStore,
    trailStore,
    hammersStore,
    puzzleStore,
    metagamesStore,
    walletStore,
    minigamesStore,
    gameUiStore,
    sportEventsStore,
    monetizationEventsStore,
  } = useStores();
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const { startAnimation } = useCoinsAnimation();
  // Single owner of the loader: two components deciding visibility is what doubled it last time.
  const [isGameReady, setIsGameReady] = useState(false);
  const [isRevealed, setIsRevealed] = useState(false);
  const [isLoaderShown, setIsLoaderShown] = useState(false);
  // Sports can ask the host to share a result. Only SportsDialog implements this, so without it here
  // sharing from a warm-launched sport event would do nothing at all.
  const [shareData, setShareData] = useState<{
    text: string;
    imageUrl: string;
    analyticsEventName?: string;
    analyticsPayload?: Record<string, unknown>;
  } | null>(null);
  const isShareSportEnabled = useFlag('share_sport_enable', false);

  // Sharing is the last step of a sport event: SportsDialog treats closing the share sheet as closing
  // the game, and the warm path has to behave the same or the frame stays live.
  const closeSportEvent = useCallback(() => {
    setIsGameReady(false);
    void monetizationEventsStore.fetch();
    void walletStore.fetchWallet();
    sportEventsStore.closeGame();
  }, [monetizationEventsStore, walletStore, sportEventsStore]);

  const onMessage = useCallback(
    (event: MessageEvent) => {
      if (event.origin !== import.meta.env.VITE_GAME_UI_URL) {
        return;
      }

      // Only this frame's own messages. Every metagame dialog listens on window, so without this the
      // warm frame and a mounted dialog would both act on the same message.
      if (iframeRef.current && event.source !== iframeRef.current.contentWindow) {
        return;
      }

      try {
        const message = typeof event.data === 'object' ? event.data : JSON.parse(event.data);
        const { type, data, payload } = message ?? {};

        if (type === 'warmReady') {
          devLog('MetagamesWarmFrame: warm frame reported ready');
          // event.source is the window that just told us it is ready — the only window we should ever
          // post a launch into.
          metagamesWarmStore.onWarmReady(event.source as Window | null);
          return;
        }

        // Prize Drop speaks `data.action`, unlike Trail/Hammers/Puzzle which use `type`. Handling only
        // its close here is what makes repeated measurement runs possible.
        // Prize Drop reports readiness as `data.action: 'loaded'`; the other three use
        // `type: 'authenticationComplete'`. Accept both so this survives being pointed at another game.
        if (data?.action === 'loaded' || type === 'authenticationComplete') {
          setIsGameReady(true);
          return;
        }

        // Trail and Puzzle launch their minigame through this, and it needs the iframe ELEMENT, not the
        // window — which is why the warm frame has to own it rather than relay the message. With no
        // dialog mounted, starting a minigame from inside Trail otherwise did nothing at all.
        // Hammers only, and only its dialog handles this — so on the warm path the reward animation
        // would simply never play.
        if (type === 'coinsAnimation') {
          const scAmount = payload?.find((r: { coinType: string }) => r.coinType === 'SC')?.coinValue || 0;
          const gcAmount = payload?.find((r: { coinType: string }) => r.coinType === 'GC')?.coinValue || 0;

          startAnimation({ rewards: [{ scCoin: Number(scAmount), gcCoin: Number(gcAmount), bundleItems: [] }] });
          return;
        }

        if (type === 'startMinigame') {
          minigamesStore.startMetagameMinigame(message.payload?.gameId, iframeRef.current);
          return;
        }

        if (type === 'vibrate') {
          window.navigator.vibrate?.(50);
          return;
        }

        // Sports only. The dialog it renders is the host's, not game-ui's, so the frame has to own it
        // the same way it owns the minigame launch and the coins animation.
        if (type === 'shareSport') {
          // With the flag off no ShareDialog renders, so nothing would ever fire its onClose and the
          // warm store would sit `live` for the rest of the session, forcing every later launch onto a
          // cold dialog. Sharing ends the event either way, so close the game instead of dropping the
          // message on the floor.
          if (!isShareSportEnabled) {
            closeSportEvent();
            return;
          }

          setShareData(payload);
          return;
        }

        // Trail closes with `type: 'closeGame'`, Prize Drop with `data.action: 'close'`. Close the game
        // that is actually live rather than assuming — activeKey is the only thing that knows.
        const isClose =
          data?.action === 'close' ||
          data?.action === 'load_timeout' ||
          type === 'closeGame' ||
          type === 'generalError';

        if (isClose) {
          setIsGameReady(false);

          // The dialogs do more than close on this message: they refetch event state and the wallet,
          // because a game can change the balance. Skipping that left a stale balance on screen after
          // playing — a money-visible bug, and silent. Sports events live in their own store, so which
          // event state to refetch follows activeKey.
          if (metagamesWarmStore.activeKey === 'sports') {
            void monetizationEventsStore.fetch();
          } else {
            void metagamesStore.fetch();
          }

          void walletStore.fetchWallet();

          // The close analytics event is sent by the dialog handler, not by closeGame() — verified. On the
          // warm path no dialog is mounted, so without this every warm close is missing from analytics,
          // and the better warm launch works the more of that data silently disappears.
          const closeAnalytics = { click_location: 'close', event_action: 'click', triggered_by: 'click' };

          // Only tell the game to close. Its own closeGame drives the frame back to idle, so every exit
          // path — including errors and programmatic closes that post no message — goes through one place.
          switch (metagamesWarmStore.activeKey) {
            case 'trail':
              trailStore.sendAnalyticsEvent(closeAnalytics);
              trailStore.closeGame();
              break;
            case 'puzzle':
              puzzleStore.sendAnalyticsEvent(closeAnalytics);
              puzzleStore.closeGame();
              break;
            case 'hammers':
              // Its dialog sends no close analytics, so neither do we.
              hammersStore.closeGame();
              break;
            case 'prizeDrop':
              prizeDropStore.sendAnalyticsEvent(closeAnalytics);
              prizeDropStore.closeGame();
              break;
            case 'sports':
              // SportsDialog sends no close analytics on this message, so neither do we — adding one
              // here would put an event on the warm path that the cold path never emits.
              sportEventsStore.closeGame();
              break;
            default:
              // activeKey is null: nothing was launched, so there is nothing to close. Falling through
              // to Prize Drop sent close analytics for a game that never opened, and called a closeGame()
              // whose setCloseDialog() could dismiss an unrelated dialog.
              break;
          }
        }
      } catch (error) {
        devError('MetagamesWarmFrame: failed to handle a message', error);
      }
    },
    [
      metagamesWarmStore,
      prizeDropStore,
      trailStore,
      hammersStore,
      puzzleStore,
      metagamesStore,
      walletStore,
      minigamesStore,
      startAnimation,
      sportEventsStore,
      monetizationEventsStore,
      isShareSportEnabled,
      closeSportEvent,
    ],
  );

  useEffect(() => {
    window.addEventListener('message', onMessage);

    return () => window.removeEventListener('message', onMessage);
  }, [onMessage]);

  useEffect(() => {
    // Two gates, and both matter.
    //
    // newGameUiAuthEnabled: getGameUiURL builds version=2 until it resolves, and a version=2 document
    // takes the legacy Firebase path — a warm frame must never be booted onto it. It doubles as a
    // readiness signal, since it only flips once LaunchDarkly has answered.
    //
    // metagames_warm_launch: the kill switch. Defaults to FALSE, so this stays off until the flag is
    // created and turned on — a second permanent game-ui instance is not something to enable by accident.
    // Warming is the only control point needed: with no warm frame, launch() returns false and every
    // caller falls through to its existing dialog.
    //
    // WARM_LAUNCH_OVERRIDE_PARAM exists because the flag does not exist in LaunchDarkly yet, and testing
    // should not require creating it. Drop it once the flag is real.
    const overridden = new URLSearchParams(window.location.search).get(WARM_LAUNCH_OVERRIDE_PARAM) === '1';

    if (!gameUiStore.newGameUiAuthEnabled || !(overridden || resolveFlag('metagames_warm_launch', false))) {
      return;
    }

    // Never boot while a game is opening or running. A second game-ui competes for bandwidth and the
    // main thread with the slot's own boot, which delays the slots handshake that flips
    // gameStore.showGameUi — leaving the host header visible under game-ui's header, and tripping the
    // iframe watchdog into a reload. Visible only on a bundled build, where the slot normally wins that
    // race comfortably.
    if (window.location.pathname.startsWith('/games')) {
      return;
    }

    // And not during the lobby's own load either: warming is worthless if it steals bandwidth from the
    // page the player is actually looking at. Wait for an idle moment.
    const start = () => metagamesWarmStore.warm();

    if (typeof requestIdleCallback === 'function') {
      const handle = requestIdleCallback(start, { timeout: 5000 });

      return () => cancelIdleCallback(handle);
    }

    const timer = setTimeout(start, 3000);

    return () => clearTimeout(timer);
  }, [gameUiStore.newGameUiAuthEnabled, metagamesWarmStore]);

  const { src, isVisible, frameKey } = metagamesWarmStore;

  useEffect(() => {
    if (!isVisible) {
      setIsRevealed(false);

      return;
    }

    if (isGameReady) {
      setIsRevealed(true);

      return;
    }

    const timer = setTimeout(() => setIsRevealed(true), REVEAL_CAP_MS);

    return () => clearTimeout(timer);
  }, [isVisible, isGameReady]);

  useEffect(() => {
    if (!isVisible || isRevealed) {
      setIsLoaderShown(false);

      return;
    }

    const timer = setTimeout(() => setIsLoaderShown(true), LOADER_DELAY_MS);

    return () => clearTimeout(timer);
  }, [isVisible, isRevealed]);

  if (!src) {
    return null;
  }

  return (
    <div className={classNames(styles.cover, { [styles.hidden]: !isVisible })}>
      {isLoaderShown && <Loader cover />}
      <iframe
        // Bumped only when a frame has to be thrown away, to force a fresh element.
        key={frameKey}
        ref={iframeRef}
        allow="autoplay; clipboard-write *; clipboard-read *"
        className={classNames(styles.frame, { [styles.framePending]: !isRevealed })}
        data-testid="metagames-warm-frame"
        src={src}
        title="metagames"
      />
      {shareData && isShareSportEnabled && (
        <ShareDialog
          imageUrl={shareData.imageUrl}
          showCopyLink={true}
          text={shareData.text}
          title={shareData.text}
          onClose={() => {
            setShareData(null);
            closeSportEvent();
          }}
          onTrackEvent={(event) => {
            if (!shareData.analyticsEventName || !shareData.analyticsPayload) {
              return;
            }

            if (event === 'impression') {
              const { button_text: _buttonText, ...rest } = shareData.analyticsPayload;

              void analytics.sendEvent(shareData.analyticsEventName as any, {
                ...rest,
                event_type: 'share-sport',
                event_action: 'impression',
              });

              return;
            }

            void analytics.sendEvent(shareData.analyticsEventName as any, {
              ...shareData.analyticsPayload,
              button_text: event,
            });
          }}
        />
      )}
    </div>
  );
});
